import React from "react";
import Badge from "@components/Badge/Badge";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import Subheader from "@components/Subheader/Subheader";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type {
  DeficiencyDetailsResolution,
  DeficiencyDetailsResponse,
  DeficiencyDocument,
  DeficiencyResolutionPayload,
  DeficiencyStatus,
  DeficiencyTask,
  DeficiencyTaskDecision,
} from "@services/deficiencies/types";
import { getDeficiencyById } from "@services/deficienciesApi";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import DeficiencyCard from "../components/DeficiencyCard";
import { useDeficiencyFlow } from "../hooks/useDeficiencyFlow";
import { useDownloadDeficiencyAttachment } from "../hooks/useDownloadDeficiencyAttachment";
import ApproveDeficiencyDrawer from "./flow/ApproveDeficiencyDrawer";
import DeficiencyFlowButtons from "./flow/DeficiencyFlowButtons";
import RejectDeficiencyDrawer from "./flow/RejectDeficiencyDrawer";
import ResolveDeficiencyDrawer from "./flow/ResolveDeficiencyDrawer";
import { clearPersistedDeficiencyDrawerUploads } from "./flow/uploadKeys";
import StatusDetailsDrawer from "./StatusDetailsDrawer";
import {
  canViewDeficiencyTaskDecisionDetails,
  formatDeficiencyDateTime,
  getDeficiencyDetailsItems,
  mapDeficiencyStatus,
} from "./utils";

const TEST_ID_BASE = "deficiencies-page";
const LANG_BASE = "deficiencies";

const wrapperStyleClass = "border-t border-[#F1F2F4] pt-3";
const wrapperFirstStyleClass = "pt-3";
const transStyleClass = "font-normal text-[#646A77]";
const FAKE_RESOLVED_STATUS: DeficiencyStatus = "AWAITING_APPROVAL";
const buildFakeTaskDecision = ({
  status,
  comments,
  attachments,
  submittedBy,
}: {
  status: "APPROVED" | "REJECTED";
  comments?: string;
  attachments: DeficiencyDocument[];
  submittedBy: string;
}): DeficiencyTaskDecision => {
  if (status === "APPROVED") {
    return {
      status: "APPROVED",
      approvalNotes: comments,
      approvalDocuments: attachments,
      submittedBy,
    };
  }

  return {
    status: "REJECTED",
    rejectionNotes: comments,
    rejectionDocuments: attachments,
    submittedBy,
  };
};

const LongTextPaperItem = ({
  testId,
  label,
  value,
}: {
  testId: string;
  label: string;
  value: string;
}) => (
  <div className="flex w-full mt-3 mb-3">
    <span
      className="w-1/4 shrink-0 font-normal text-[#646A77]"
      data-testid={`${testId}-label`}
      style={{ whiteSpace: "pre-line" }}
    >
      {label}
    </span>
    <span
      className="w-3/4 font-normal"
      data-testid={`${testId}-value`}
      style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
    >
      {value}
    </span>
  </div>
);

const DeficienciesDetails: React.FC = () => {
  const { handleDownload, handlePreview } = useDownloadDeficiencyAttachment();
  const { i18n, t } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { id } = useParams();
  const submittedBy = useAuthStore((state) => state.user?.preferredUsername ?? "");
  const currentUserClaimOwnerName = useAuthStore(
    (state) => state.user?.email ?? state.user?.preferredUsername ?? "",
  );
  const [fakeStatus, setFakeStatus] = React.useState<DeficiencyStatus | null>(null);
  const [fakeResolution, setFakeResolution] = React.useState<DeficiencyDetailsResolution | null>(
    null,
  );
  const [fakeTask, setFakeTask] = React.useState<DeficiencyTask | null>(null);
  const [fakeTaskDecision, setFakeTaskDecision] = React.useState<DeficiencyTaskDecision | null>(
    null,
  );
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = React.useState(false);

  const { data: deficiencyDetailsResponse, refetch: refetchDeficiency } =
    useQuery<DeficiencyDetailsResponse>({
      queryKey: ["Deficiency", id],
      queryFn: () => {
        return getDeficiencyById(id as string);
      },
      enabled: !!id,
      refetchInterval: 60_000,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    });

  const handleResolveSuccess = React.useCallback(
    ({
      id: resolvedDeficiencyId,
      payload,
    }: {
      id: string;
      payload: DeficiencyResolutionPayload;
    }) => {
      if (!id || resolvedDeficiencyId !== id) {
        return;
      }

      setFakeStatus(FAKE_RESOLVED_STATUS);
      setFakeResolution({
        id: `local-resolution-${resolvedDeficiencyId}`,
        explanation: payload.explanation,
        documents: payload.documents,
        createdAt: new Date().toISOString(),
        createdBy: "",
      });

      void refetchDeficiency();
    },
    [id, refetchDeficiency],
  );

  const {
    openDrawers,
    openDrawer,
    closeDrawer,
    handleResolveSubmit,
    handleClaimDeficiency,
    handleUnclaimDeficiency,
    handleApproveSubmit,
    handleRejectSubmit,
    isResolvingDeficiency,
    isCompletingDeficiency,
  } = useDeficiencyFlow({ onResolveSuccess: handleResolveSuccess });
  const breadcrumbLabel = t(`${LANG_BASE}.breadcrumb`);

  React.useEffect(() => {
    return () => {
      clearPersistedDeficiencyDrawerUploads();
    };
  }, [id]);

  React.useEffect(() => {
    if (!fakeResolution) {
      return;
    }

    if (!deficiencyDetailsResponse?.resolution) {
      return;
    }

    if (deficiencyDetailsResponse.status === FAKE_RESOLVED_STATUS) {
      setFakeStatus(null);
      setFakeResolution(null);
    }
  }, [deficiencyDetailsResponse, fakeResolution]);

  React.useEffect(() => {
    if (!fakeTask) {
      return;
    }

    if (
      deficiencyDetailsResponse?.task?.status === fakeTask.status &&
      deficiencyDetailsResponse?.task?.claimOwnerName === fakeTask.claimOwnerName
    ) {
      setFakeTask(null);
    }
  }, [
    deficiencyDetailsResponse?.task?.claimOwnerName,
    deficiencyDetailsResponse?.task?.status,
    fakeTask,
  ]);

  React.useEffect(() => {
    if (!fakeTaskDecision) {
      return;
    }

    const serverTaskDecision = deficiencyDetailsResponse?.taskDecision;

    if (
      serverTaskDecision?.status === fakeTaskDecision.status &&
      (!fakeStatus || deficiencyDetailsResponse?.status === fakeStatus)
    ) {
      setFakeStatus(null);
      setFakeTaskDecision(null);
    }
  }, [
    deficiencyDetailsResponse?.status,
    deficiencyDetailsResponse?.taskDecision,
    fakeStatus,
    fakeTaskDecision,
  ]);

  const effectiveDeficiencyDetailsResponse = React.useMemo(() => {
    if (!deficiencyDetailsResponse) {
      return deficiencyDetailsResponse;
    }

    if (!fakeStatus && !fakeResolution && !fakeTask && !fakeTaskDecision) {
      return deficiencyDetailsResponse;
    }

    return {
      ...deficiencyDetailsResponse,
      status: fakeStatus ?? deficiencyDetailsResponse.status,
      resolution: fakeResolution ?? deficiencyDetailsResponse.resolution,
      task: fakeTask ?? deficiencyDetailsResponse.task,
      taskDecision: fakeTaskDecision ?? deficiencyDetailsResponse.taskDecision,
    };
  }, [deficiencyDetailsResponse, fakeResolution, fakeStatus, fakeTask, fakeTaskDecision]);

  const status = mapDeficiencyStatus(effectiveDeficiencyDetailsResponse?.status, t);
  const deficiencyDetails = getDeficiencyDetailsItems({
    details: effectiveDeficiencyDetailsResponse,
    TEST_ID_BASE,
    t,
  });
  const deficiencyFlowData = effectiveDeficiencyDetailsResponse
    ? {
        id: effectiveDeficiencyDetailsResponse.id,
        status: effectiveDeficiencyDetailsResponse.status,
        task: effectiveDeficiencyDetailsResponse.task ?? null,
      }
    : undefined;
  const currentTask = effectiveDeficiencyDetailsResponse?.task ?? null;
  const effectiveTaskDecision = effectiveDeficiencyDetailsResponse?.taskDecision ?? null;
  const shouldShowViewDetails = canViewDeficiencyTaskDecisionDetails(effectiveTaskDecision);
  const deficiencyDocuments = effectiveDeficiencyDetailsResponse?.documents ?? [];
  const resolution = effectiveDeficiencyDetailsResponse?.resolution;
  const resolutionDocuments = resolution?.documents ?? [];
  const handleClaim = React.useCallback(() => {
    void handleClaimDeficiency(deficiencyFlowData?.id)
      .then(() => {
        if (!currentTask) {
          return;
        }

        setFakeTask({
          ...currentTask,
          status: "CLAIMED",
          claimOwnerName: currentUserClaimOwnerName || currentTask.claimOwnerName,
        });
        void refetchDeficiency();
      })
      .catch(() => {});
  }, [
    currentTask,
    currentUserClaimOwnerName,
    deficiencyFlowData?.id,
    handleClaimDeficiency,
    refetchDeficiency,
  ]);

  const handleUnclaim = React.useCallback(() => {
    void handleUnclaimDeficiency(deficiencyFlowData?.id)
      .then(() => {
        if (!currentTask) {
          return;
        }

        setFakeTask({
          ...currentTask,
          status: "READY",
          claimOwnerName: undefined,
        });
        void refetchDeficiency();
      })
      .catch(() => {});
  }, [currentTask, deficiencyFlowData?.id, handleUnclaimDeficiency, refetchDeficiency]);

  const handleApprove = React.useCallback(
    async ({
      deficiencyId,
      comments,
      attachments,
    }: {
      deficiencyId?: string;
      comments?: string;
      attachments: DeficiencyDocument[];
    }) => {
      await handleApproveSubmit({
        deficiencyId,
        comments,
        attachments,
      });

      setFakeStatus("RESOLVED");
      setFakeTaskDecision(
        buildFakeTaskDecision({
          status: "APPROVED",
          comments,
          attachments,
          submittedBy,
        }),
      );

      if (currentTask) {
        setFakeTask({
          ...currentTask,
          status: "COMPLETED",
        });
      }

      void refetchDeficiency();
    },
    [currentTask, handleApproveSubmit, refetchDeficiency, submittedBy],
  );

  const handleReject = React.useCallback(
    async ({
      deficiencyId,
      comments,
      attachments,
    }: {
      deficiencyId?: string;
      comments?: string;
      attachments: DeficiencyDocument[];
    }) => {
      await handleRejectSubmit({
        deficiencyId,
        comments,
        attachments,
      });

      setFakeStatus("PENDING_RESOLUTION");
      setFakeTaskDecision(
        buildFakeTaskDecision({
          status: "REJECTED",
          comments,
          attachments,
          submittedBy,
        }),
      );

      if (currentTask) {
        setFakeTask({
          ...currentTask,
          status: "COMPLETED",
        });
      }

      void refetchDeficiency();
    },
    [currentTask, handleRejectSubmit, refetchDeficiency, submittedBy],
  );
  const shouldShowResolution =
    resolution && effectiveDeficiencyDetailsResponse?.status !== "PENDING_RESOLUTION";
  return (
    <PageLayout dir={dir}>
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        subtitle={
          <div
            className="flex items-center space-x-2 text-sm text-gray-700"
            data-testid={`${TEST_ID_BASE}-breadcrumb`}
          >
            <Link
              to="/deficiencies"
              className="text-blue-600 hover:underline"
              data-testid={`${TEST_ID_BASE}-breadcrumb-link`}
            >
              {breadcrumbLabel}
            </Link>
            <span className="text-gray-400">/</span>
            <span data-testid={`${TEST_ID_BASE}-breadcrumb-current`}>
              {t(`${LANG_BASE}.details-title`, {
                id: effectiveDeficiencyDetailsResponse?.refOrderNumber,
              })}
            </span>
          </div>
        }
        title={
          <div className="flex justify-between items-end mt-1 w-full">
            <div>
              <span data-testid={`${TEST_ID_BASE}-title`} className="break-all">
                {t(`${LANG_BASE}.details-title`, {
                  id: effectiveDeficiencyDetailsResponse?.refOrderNumber,
                })}
              </span>
              <div className="mt-1 flex items-center">
                <div className="flex items-center">
                  <Badge type={status.type} label={status.label} bold={false} />

                  {shouldShowViewDetails && (
                    <>
                      <span className="mx-1 text-gray-500 font-normal">-</span>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsDetailsDrawerOpen(true);
                        }}
                        className="text-sm text-blue-800 hover:underline"
                        data-testid={`${TEST_ID_BASE}-view-details-link`}
                      >
                        {t("common.view-details")}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
            <DeficiencyFlowButtons
              deficiencyFlowData={deficiencyFlowData}
              openDrawer={openDrawer}
              onClaim={handleClaim}
              onUnclaim={handleUnclaim}
              onApprove={() => openDrawer("approveDrawer")}
              onReject={() => openDrawer("rejectDrawer")}
            />
          </div>
        }
      />
      <ApproveDeficiencyDrawer
        isOpen={openDrawers.approveDrawer}
        deficiencyId={deficiencyFlowData?.id}
        onClose={closeDrawer}
        onSubmit={handleApprove}
        isSubmitting={isCompletingDeficiency}
      />
      <RejectDeficiencyDrawer
        isOpen={openDrawers.rejectDrawer}
        deficiencyId={deficiencyFlowData?.id}
        onClose={closeDrawer}
        onSubmit={handleReject}
        isSubmitting={isCompletingDeficiency}
      />
      <ResolveDeficiencyDrawer
        isOpen={openDrawers.resolveDrawer}
        onClose={closeDrawer}
        deficiencyId={deficiencyFlowData?.id}
        onSubmit={handleResolveSubmit}
        isSubmitting={isResolvingDeficiency}
      />
      <StatusDetailsDrawer
        isOpen={isDetailsDrawerOpen}
        onClose={() => setIsDetailsDrawerOpen(false)}
        taskDecision={effectiveTaskDecision}
      />

      <div data-testid={TEST_ID_BASE} className="flex flex-col w-full h-full p-8 px-24">
        <div className="mb-8" data-testid={`${TEST_ID_BASE}-section-company-details`}>
          <Paper variant="outlined">
            <p className="font-bold mb-4">
              {t(`${LANG_BASE}.deficiency-details.deficiency-title`)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                {deficiencyDetails.leftColumn.map((item, index) => (
                  <PaperItem
                    key={item.testId}
                    testId={item.testId}
                    translate={item.label}
                    text={item.value}
                    textStyles="font-normal"
                    tranStyle={transStyleClass}
                    wrapperStyle={index === 0 ? wrapperFirstStyleClass : wrapperStyleClass}
                  />
                ))}
              </div>

              <div>
                {deficiencyDetails.rightColumn.map((item, index) => (
                  <PaperItem
                    key={item.testId}
                    testId={item.testId}
                    translate={item.label}
                    text={item.value}
                    textStyles="font-normal"
                    tranStyle={transStyleClass}
                    wrapperStyle={index === 0 ? wrapperFirstStyleClass : wrapperStyleClass}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-[#F1F2F4]">
              <LongTextPaperItem
                testId={`${TEST_ID_BASE}-details`}
                label={t(`${LANG_BASE}.deficiency-details.details`)}
                value={deficiencyDetails.detailsText || "-"}
              />
            </div>
            <div className="border-t border-[#F1F2F4]">
              <LongTextPaperItem
                testId={`${TEST_ID_BASE}-remarks`}
                label={t(`${LANG_BASE}.deficiency-details.remarks`)}
                value={deficiencyDetails.remarksText || "-"}
              />
            </div>
          </Paper>
        </div>
        {deficiencyDocuments.length > 0 && (
          <div className="mb-8">
            <h2
              className="text-base text-[#646A77] mb-4"
              data-testid={`${TEST_ID_BASE}-attachments`}
            >
              {t(`${LANG_BASE}.attachments`)}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {deficiencyDocuments.map((document, index) => (
                <DeficiencyCard
                  key={`${document.documentUrl}-${index}`}
                  testId={`${TEST_ID_BASE}-document-${index}`}
                  fileName={document.documentName || ""}
                  fileUrl={document.documentUrl}
                  onPreview={() => handlePreview("bff-deficiency", document.documentUrl || "")}
                  onDownload={() =>
                    handleDownload(
                      "bff-deficiency",
                      document.documentUrl || "",
                      document.documentName || "",
                    )
                  }
                />
              ))}
            </div>
          </div>
        )}

        {shouldShowResolution && (
          <>
            <div className="mb-8" data-testid={`${TEST_ID_BASE}-section-resolution-details`}>
              <Paper variant="outlined">
                <p className="font-bold mb-4">{t(`${LANG_BASE}.resolution-details.title`)}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <PaperItem
                      testId={`${TEST_ID_BASE}-resolution-date-time`}
                      translate={t(`${LANG_BASE}.resolution-details.date-time`)}
                      text={formatDeficiencyDateTime(resolution.createdAt)}
                      textStyles="font-normal"
                      tranStyle={transStyleClass}
                      wrapperStyle={wrapperFirstStyleClass}
                    />
                  </div>
                </div>

                <div className="border-t border-[#F1F2F4]">
                  <LongTextPaperItem
                    testId={`${TEST_ID_BASE}-resolution-details`}
                    label={t(`${LANG_BASE}.deficiency-details.details`)}
                    value={resolution.explanation || "-"}
                  />
                </div>
              </Paper>
            </div>
            {resolutionDocuments.length > 0 && (
              <div className="mb-8">
                <h2
                  className="text-base text-[#646A77] mb-4"
                  data-testid={`${TEST_ID_BASE}-resolution-attachments`}
                >
                  {t("deficiencies.resolve-drawer.fields.resolution-proof")}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {resolutionDocuments.map((document, documentIndex) => (
                    <DeficiencyCard
                      key={`${document.documentUrl}-${documentIndex}`}
                      testId={`${TEST_ID_BASE}-resolution-document-${documentIndex}`}
                      fileName={document.documentName || ""}
                      fileUrl={document.documentUrl}
                      onPreview={() => handlePreview("bff-deficiency", document.documentUrl || "")}
                      onDownload={() =>
                        handleDownload(
                          "bff-deficiency",
                          document.documentUrl || "",
                          document.documentName || "",
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default DeficienciesDetails;
